<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    offset-y
    bottom
    left
    :min-width="menuWidth"
    :max-width="menuWidth"
    content-class="product-switcher-menu"
  >
    <template #activator="{ on, attrs }">
      <button
        class="product-switcher-btn"
        :class="{ 'product-switcher-btn--active': menu }"
        v-bind="attrs"
        v-on="on"
      >
        <lucide-icon name="Grid3x3" :size="20" />
      </button>
    </template>

    <v-card class="product-switcher-card">
      <v-card-text class="pa-0">
        <div class="portal-grid">
          <div
            v-for="(product, index) in products"
            :key="product.name"
            :ref="index === 0 ? 'firstProductCard' : undefined"
            class="portal-card"
            :class="{ 'portal-card--disabled': product.disabled }"
            :tabindex="product.disabled ? -1 : 0"
            role="button"
            :aria-label="`Navigate to ${product.name}`"
            @click="handleProductClick(product)"
            @keydown.enter="handleProductClick(product)"
            @keydown.space.prevent="handleProductClick(product)"
          >
            <div class="portal-card-content">
              <div class="portal-icon-wrapper">
                <lucide-icon
                  :name="product.icon"
                  :size="28"
                  class="portal-icon"
                />
              </div>
              <div class="portal-name">
                {{ product.name }}
              </div>
              <div v-if="product.description" class="portal-description">
                {{ product.description }}
              </div>
              <div class="portal-actions">
                <lucide-icon name="ExternalLink" :size="18" class="portal-action-icon" />
                <lucide-icon name="ChevronRight" :size="18" class="portal-action-icon" />
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import Vue from 'vue';
import LucideIcon from '../LucideIcon';

export interface Product {
  name: string;
  icon: string;
  description?: string;
  disabled?: boolean;
  href?: string;
  action?: string;
  targetApp?: string; // For exchange token service integration
}

export default Vue.extend({
  name: 'ProductSwitcher',

  components: {
    LucideIcon
  },

  props: {
    products: {
      type: Array as () => Product[],
      default: () => []
    },
    // Optional custom navigation handler for exchange token integration
    navigationHandler: {
      type: Function as unknown as () => (product: Product) => Promise<void> | void,
      default: null
    }
  },

  data() {
    return {
      menu: false
    };
  },

  computed: {
    menuWidth(): number {
      return 340; // Width to match OverviewCard style
    }
  },

  watch: {
    menu(newVal: boolean) {
      if (newVal) {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.firstProductCard) {
              const cardElement = Array.isArray(this.$refs.firstProductCard)
                ? this.$refs.firstProductCard[0]
                : this.$refs.firstProductCard;
              const el = (cardElement as any)?.$el || cardElement;
              if (el && (el as HTMLElement).focus) {
                (el as HTMLElement).focus();
                return;
              }
            }
            const firstCard = document.querySelector('.portal-card:not([disabled])') as HTMLElement;
            if (firstCard) {
              firstCard.focus();
            }
          }, 150);
        });
      }
    }
  },

  methods: {
    async handleProductClick(product: Product) {
      if (product.disabled) return;

      this.$emit('product-click', product);

      // If custom navigation handler provided (e.g., for exchange token service)
      if (this.navigationHandler) {
        await this.navigationHandler(product);
        this.menu = false;
        return;
      }

      // Emit action event if specified
      if (product.action) {
        this.$emit(product.action, product);
      }

      // Simple href navigation for Storybook/demos
      if (product.href) {
        window.location.href = product.href;
      }

      this.menu = false;
    }
  }
});
</script>

<style scoped lang="scss">
.product-switcher-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  background: none;
  border: none;
  border-radius: var(--radius);
  color: var(--global-header-text-color, #6b7280);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &--active {
    background-color: var(--global-muted, #f3f4f6);
    color: var(--global-primary, #406A94);
  }
}

.product-switcher-card {
  display: flex;
  background-color: #ffffff;
  background-color: var(--card, #ffffff);

  ::v-deep .v-card__text {
    display: flex;
    justify-content: center;
    width: 100%;
  }
}

.portal-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  width: 100%;
}

.portal-card {
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: var(--global-card-background, #ffffff);
  border: 1px solid var(--global-border-color, #e5e7eb);
  border-left: 4px solid var(--global-primary, #406A94);
  border-radius: var(--radius, 8px);
  outline: none;

  &:hover:not(.portal-card--disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:focus:not(.portal-card--disabled),
  &:focus-visible {
    // No focus ring - all cards look identical
    box-shadow: none !important;
    outline: none !important;
  }

  &.portal-card--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.portal-card-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 140px;
}

.portal-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background-color: var(--global-muted, #f3f4f6);
  border-radius: var(--radius, 8px);
  margin-bottom: 16px;
}

.portal-icon {
  color: var(--global-primary, #406A94);
}

.portal-name {
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: .875rem;
  font-weight: 600;
  color: var(--global-header-text-color, #1f2937);
  line-height: 1.4;
  margin: 0 0 8px 0;
}

.portal-description {
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--global-muted-foreground, #6b7280);
  line-height: 1.5;
  margin: 0;
  flex-grow: 1;
}

.portal-actions {
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 2px;
}

.portal-action-icon {
  color: var(--global-muted-foreground, #9ca3af);
}
</style>

<!-- Unscoped styles for dark mode and menu dropdown -->
<style lang="scss">
// Ensure menu content is positioned correctly with proper z-index
.v-menu__content.product-switcher-menu {
  margin-top: 8px !important;
  background-color: #ffffff;
  background-color: var(--card, #ffffff);
  border-radius: var(--radius);
  border: 1px solid #e5e7eb;
  border-color: var(--border, #e5e7eb);
  overflow: visible !important;
  z-index: 9999 !important;

  // Visual anchor arrow pointing up
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    right: 20px;
    width: 12px;
    height: 12px;
    background-color: #ffffff;
    background-color: var(--card, #ffffff);
    border-left: 1px solid #e5e7eb;
    border-left-color: var(--border, #e5e7eb);
    border-top: 1px solid #e5e7eb;
    border-top-color: var(--border, #e5e7eb);
    transform: rotate(45deg);
    z-index: 1;
  }
}

// Dark mode overrides - use .theme--dark for Vuetify menus (rendered at body level)
.dark,
.theme--dark {
  .product-switcher-btn {
    color: var(--global-foreground, #ffffff) !important;

    &:hover {
      background-color: rgba(255, 255, 255, 0.04) !important;
    }

    &.product-switcher-btn--active {
      background-color: rgba(255, 255, 255, 0.08) !important;
      color: var(--global-primary, #ed303e) !important;
    }
  }

  // Menu overlay uses main background (same as splash page)
  .product-switcher-card {
    background-color: var(--global-main-background-color, #121214) !important;
  }

  .product-switcher-menu,
  .v-menu__content.product-switcher-menu {
    background-color: var(--global-main-background-color, #121214) !important;
    border-color: var(--global-border-color, #2e2e32) !important;

    // Dark mode arrow anchor
    &::before {
      background-color: var(--global-main-background-color, #121214) !important;
      border-left-color: var(--global-border-color, #2e2e32) !important;
      border-top-color: var(--global-border-color, #2e2e32) !important;
    }
  }

  // All portal cards look identical - no highlighting for current/focused
  .portal-card {
    background-color: var(--global-card-background, #1c1c1e) !important;
    border: 1px solid var(--global-border-color, #2e2e32) !important;
    border-left: 4px solid var(--global-primary, #ed303e) !important;

    &:hover:not(.portal-card--disabled) {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
    }

    // Remove focus ring - all cards should look identical
    &:focus,
    &:focus-visible,
    &:focus:not(.portal-card--disabled) {
      box-shadow: none !important;
      outline: none !important;
    }
  }

  .portal-icon-wrapper {
    background-color: rgba(255, 255, 255, 0.08) !important;
  }

  .portal-name {
    color: var(--global-foreground, #f5f5f5) !important;
  }

  .portal-description {
    color: var(--global-muted-foreground, #9ca3af) !important;
  }

  .portal-icon {
    color: var(--global-primary, #ed303e) !important;
  }

  .portal-action-icon {
    color: var(--global-muted-foreground, #6b7280) !important;
  }

  // Override Vuetify card background in dark mode
  .v-card.product-switcher-card {
    background-color: var(--global-main-background-color, #121214) !important;
  }
}

// Target menu content directly when it has theme--dark
.product-switcher-menu.theme--dark,
.theme--dark .product-switcher-menu,
.v-menu__content.product-switcher-menu.theme--dark,
.theme--dark .v-menu__content.product-switcher-menu {
  background-color: var(--global-main-background-color, #121214) !important;

  &::before {
    background-color: var(--global-main-background-color, #121214) !important;
    border-left-color: var(--global-border-color, #2e2e32) !important;
    border-top-color: var(--global-border-color, #2e2e32) !important;
  }

  .portal-card {
    background-color: #1c1c1e !important;
    border: 1px solid #2e2e32 !important;
    border-left: 4px solid #ed303e !important;

    // Remove focus ring - all cards should look identical
    &:focus,
    &:focus-visible {
      box-shadow: none !important;
      outline: none !important;
    }
  }

  .portal-icon-wrapper {
    background-color: rgba(255, 255, 255, 0.08) !important;
  }

  .portal-name {
    color: #f5f5f5 !important;
  }

  .portal-description {
    color: #9ca3af !important;
  }

  .portal-icon {
    color: #ed303e !important;
  }
}
</style>
